import { useEffect, useRef, ChangeEvent, SubmitEvent } from 'react'
import type { RowData } from '@tanstack/react-table'
import type { TableWrapperTable } from '@/lib/table/table'
import {
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/solid'
import { className2 } from '@/components/common/styles'

type Props<T extends RowData> = {
  table: TableWrapperTable<T>
  name: string
}

const Pagination = <T extends RowData>({ table, name }: Props<T>) => {
  const inputRef = useRef<HTMLInputElement>(null)

  // @@TODO: Check in other files if ChangeEvent is used instead of SubmitEvent
  const handlePage = (e: ChangeEvent<HTMLInputElement>) => {
    // NaN allows editing, e.g. temporarily empty when deleting input.
    let page = e.target.value ? e.target.valueAsNumber - 1 : Number.NaN
    if (Number.isNaN(page)) {
      return
    }

    const maxPage = table.getPageCount() - 1
    if (page < 0) page = 0
    if (page > maxPage) page = maxPage

    e.target.value = String(page + 1)
    table.setPageIndex(page)
  }

  const pageIndex = table.state.pagination.pageIndex

  // If page gets changed by First, Last, Previous, Next, then set input number input field.
  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.value = String(pageIndex + 1)
    }
  }, [pageIndex])

  const handleForm = (e: SubmitEvent<HTMLFormElement>) => {
    console.log('handleForm=', e)
  }

  const arialabel = `pagination for ${name}`

  return (
    <div>
      <div className="">
        <nav aria-label={arialabel}>
          <div className="flex items-center justify-between">
            <div className="flex justify-start px-4 gap-x-4">
              <div className="grid grid-cols-2">
                <div className="col-span-2 sm:col-span-1">
                  <button
                    type="button"
                    className={`${className2} rounded-md py-2 px-3`}
                    onClick={() => {
                      table.setPageIndex(0)
                    }}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <ChevronDoubleLeftIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                    />
                    <span className="sr-only">First</span>
                  </button>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <button
                    type="button"
                    className={`${className2} py-2 px-3`}
                    onClick={() => {
                      table.previousPage()
                    }}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
                    <span className="sr-only">Previous</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end px-4 gap-x-4">
              <div className="grid grid-cols-2">
                <div className="col-span-2 sm:col-span-1">
                  <button
                    type="button"
                    className={`${className2} py-2 px-3`}
                    onClick={() => {
                      table.nextPage()
                    }}
                    disabled={!table.getCanNextPage()}
                  >
                    <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
                    <span className="sr-only">Next</span>
                  </button>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <button
                    type="button"
                    className={`${className2} py-2 px-3`}
                    onClick={() => {
                      table.setPageIndex(table.getPageCount() - 1)
                    }}
                    disabled={!table.getCanNextPage()}
                  >
                    <ChevronDoubleRightIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Last</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>
      {/* ================================================== */}
      <div className="grid grid-cols-3 text-gray-500 dark:text-gray-400 text-sm">
        {/* ================================================== */}
        {/** 
      <form
        //onChange={e => handleForm(e)}
      >
      */}
        <div className="col-span-3 lg:col-span-1">
          <div className="flex items-center h-14 px-4">
            <div className="mr-2">
              {table.getPageCount() ? (
                <div className="w-20 inline-flex">
                  {'Page'}&nbsp;{pageIndex + 1}
                  &nbsp;
                  {'of'}&nbsp;{table.getPageCount()}
                </div>
              ) : (
                <div>
                  {'No'}&nbsp;{'pages'}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* ================================================== */}
        <div className="col-span-3 lg:col-span-1">
          <div className="flex items-center h-14 gap-x-2 px-4">
            <div className="" id="go-to-page">
              Go&nbsp;to&nbsp;page:
            </div>
            <input
              aria-labelledby="go-to-page"
              // NOTE: Not really a number in DOM, but a string
              type="number"
              ref={inputRef}
              defaultValue={pageIndex + 1}
              onChange={(e) => handlePage(e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                }
              }}
              className="border p-1 rounded-sm w-16 bg-white dark:bg-black text-black dark:text-white"
            />
          </div>
        </div>
        {/* ================================================== */}
        <div className="col-span-3 lg:col-span-1">
          <div className="flex items-center h-14 gap-x-2 px-4">
            <div className="" id="items-per-page">
              Items&nbsp;per&nbsp;page:
            </div>
            <select
              aria-labelledby="items-per-page"
              value={table.state.pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value))
              }}
              className="border p-1 rounded-sm bg-white dark:bg-black text-black dark:text-white"
            >
              {[1, 5, 10, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
        {/*
    </form>
    */}
      </div>
    </div>
  )
}

export default Pagination
